import Cookies from 'js-cookie';
import {
  AUTHENTICATE,
  AUTHENTICATE_WITH_2FA,
  AUTHENTICATION_FAILED,
  AUTHENTICATION_IN_PROGRESS,
  AUTHENTICATION_WITH_2FA_IN_PROGRESS,
  AuthErrorObject,
  AuthenticationAction,
  CANCEL_TWO_FACTOR_AUTHENTICATION_IN_PROGRESS,
  IAuthenticate,
  IAuthenticateWith2FA,
  IAuthenticationInProgress,
  IAuthenticationWith2FAInProgress,
  ICancel2FAInProgress,
  IUnauthenticate,
  LoginCredentials,
  LoginCredentials2FAWithTokens,
  TWO_FACTOR_AUTHENTICATION_FAILED,
  UNAUTHENTICATE,
  VerficationDetails,
} from '../../types/redux/auth/authTypes';
import { ThunkDispatch as Dispatch } from 'redux-thunk';
import Risksystem3PublicAuthService, {
  FastApiFetchError,
} from '../../services/risksystem3-public-auth-service';
import RisksystemHgAuthService from '../../services/risksystem-hg-auth-service';
import TokenService from '../../services/token-service';
import { formSafeAppend } from '../../utilities/helpers/forms/formUtils';
import { AxiosError } from 'axios';
import FastAPIInstance from '../../services/risksystem3-public-api';
import axios from 'axios';
import { updateSentryUserContext } from '../../utilities/sentry';
import { reportError, ErrorType } from '../../utilities/errorReporting/errorReporting';

// ------------------ Common Code ------------------ //

const handleLoginSuccess = async (
  dispatch: Dispatch<AuthenticationAction, {}, any>,
  accessToken: string,
  user_name: string,
  client_name: string,
  config_name: string,
) => {
  // First, fetch the config to verify it exists before saving user info
  const cancelToken = axios.CancelToken;
  const source = cancelToken.source();

  try {
    // Fetch the config directly to verify it exists
    const configUrl = `en/${config_name}.json`;
    const isProduction = process.env.NODE_ENV === 'production';

    let configFetchSuccess = false;

    if (!isProduction && config_name) {
      // In development, try to import the local config
      try {
        await import(`../../client_configurations/config/${config_name}.json`);
        configFetchSuccess = true;
      } catch (err) {
        configFetchSuccess = false;
      }
    } else {
      // In production, fetch from the server
      try {
        await RisksystemHgAuthService.getRaptorConfig(configUrl, source.token);
        configFetchSuccess = true;
      } catch (err) {
        configFetchSuccess = false;
      }
    }

    if (!configFetchSuccess) {
      // Config fetch failed - fail the login without saving user info
      Risksystem3PublicAuthService.FastAPILogout();
      Cookies.remove('user_id');
      Cookies.remove('dev_user_id');
      return dispatch(
        authenticationFailed({
          detail:
            'We could not load your configuration. Please contact RiskSystem support.',
        }),
      );
    }

    if (!TokenService.getLocalRefreshToken()) {
      Risksystem3PublicAuthService.FastAPILogout();
      Cookies.remove('user_id');
      Cookies.remove('dev_user_id');
      return dispatch(
        authenticationFailed({
          detail:
            'Session could not be established. Please log in again.',
        }),
      );
    }

    const regex = /^https:\/\/dev2\w*\.risksystem\.com.*/;

    if (process.env.NODE_ENV === 'development') {
      Cookies.set('user_id', 'development');
    } else if (regex.test(window.location.href)) {
      const domain = new URL(window.location.href).hostname;
      Cookies.set('dev_user_id', 'development', {
        domain,
        path: '/',
        secure: true,
        sameSite: 'None',
        expires: 30,
      });
    }

    TokenService.setAccessToken(accessToken);

    dispatch(authenticate(user_name, client_name, config_name));

    // Update Sentry user context after successful login
    updateSentryUserContext();
  } catch (error) {
    // If any error occurs during config fetch, fail the login
    Risksystem3PublicAuthService.FastAPILogout();
    Cookies.remove('user_id');
    Cookies.remove('dev_user_id');
    return dispatch(
      authenticationFailed({
        detail:
          'We could not load your configuration. Please contact RiskSystem support.',
      }),
    );
  }
};

// ------------------ General Authentication Actions ------------------ //

export function unauthenticate(): IUnauthenticate {
  return {
    type: UNAUTHENTICATE,
  };
}

export function authenticate(
  user_id: string,
  client_name: string,
  config_name: string,
): IAuthenticate {
  return {
    type: AUTHENTICATE,
    user_id: user_id,
    client_name: client_name,
    config_name: config_name,
  };
}

export function authenticationInProgress(): IAuthenticationInProgress {
  return {
    type: AUTHENTICATION_IN_PROGRESS,
  };
}

export function authenticationFailed(errorMessage: AuthErrorObject) {
  return {
    type: AUTHENTICATION_FAILED,
    payload: errorMessage,
  };
}

export function logOut() {
  return async (dispatch: Dispatch<AuthenticationAction, {}, any>) => {
    Risksystem3PublicAuthService.FastAPILogout();
    Cookies.remove('user_id');
    localStorage.clear();
    dispatch({ type: 'USER_LOGOUT' });
    
    // Clear Sentry user context on logout
    updateSentryUserContext();

    await RisksystemHgAuthService.logout();
  };
}

export function logIn(credentials: LoginCredentials) {
  return async (dispatch: Dispatch<AuthenticationAction, {}, any>) => {
    // Set Authentication In Progress
    dispatch(authenticationInProgress());

    try {
      const formData = new FormData();
      formData.append('username', credentials.username);
      formData.append('password', credentials.password);
      formData.append('grant_type', 'password');

      const fastAPILoginResponse = await Risksystem3PublicAuthService.FastAPIlogin(formData);
      const { loginResponse, decryptedToken } = fastAPILoginResponse;
      const accessToken = loginResponse.data.access_token;

      if (decryptedToken.is_email_needed || decryptedToken.is_qr_code_needed) {
        handleTwoFactorAuthentication(
          dispatch,
          credentials.username,
          accessToken,
          loginResponse.data.auth_key,
          decryptedToken,
        );
        return;
      }

      const sessionResponse = await RisksystemHgAuthService.bootstrapApiSession(
        accessToken,
      );

      if (sessionResponse.data.login_return_code !== 0) {
        Risksystem3PublicAuthService.FastAPILogout();
        Cookies.remove('user_id');
        dispatch(unauthenticate());
        return;
      }

      try {
        const userInfoResponse =
          await Risksystem3PublicAuthService.fetchFastAPIUserInfo(
            loginResponse,
            decryptedToken.user_id,
          );

        handleLoginSuccess(
          dispatch,
          accessToken,
          userInfoResponse.data.user_name,
          sessionResponse.data.client_name ?? '',
          loginResponse.data.config_name ?? '',
        );
      } catch (error) {
        if (error instanceof FastApiFetchError) {
          Risksystem3PublicAuthService.FastAPILogout();
          Cookies.remove('user_id');
          return dispatch(
            authenticationFailed(
              error.responseData || { detail: error.message },
            ),
          );
        }
        throw error;
      }
    } catch (error) {
      if (error instanceof FastApiFetchError) {
        const newError = error as FastApiFetchError;
        const errorDisplay = newError.responseData || {
          detail: newError.message,
        };
        Risksystem3PublicAuthService.FastAPILogout();
        Cookies.remove('user_id');

        return dispatch(authenticationFailed(errorDisplay));
      }
      handleLoginErrorResponse(dispatch, error);
    }
  };
}

export function logInWith2FA(credentials: LoginCredentials2FAWithTokens) {
  return async (dispatch: Dispatch<AuthenticationAction, {}, any>) => {
    dispatch(
      authenticationWith2FAInProgress(
        credentials.emailCode !== '',
        credentials.qrCode !== '',
        credentials.username,
        '',
        '',
        credentials.accessToken || '',
        credentials.authKey || '',
      ),
    );

    try {
      const formData = new FormData();
      formSafeAppend(formData, 'auth_key', credentials.authKey || '');
      formSafeAppend(
        formData,
        'email_verification_code',
        credentials.emailCode,
      );
      formSafeAppend(formData, 'qr_code_verification_code', credentials.qrCode);

      const fastAPI2faLoginResponse = await Risksystem3PublicAuthService.FastAPIlogin2fa(
        credentials.accessToken || '',
        formData,
      );

      const newAccessToken =
        fastAPI2faLoginResponse.loginResponse.data.access_token;
      const sessionResponse =
        await RisksystemHgAuthService.bootstrapApiSession(newAccessToken);

      if (sessionResponse.data.login_return_code === 0) {
        try {
          const userInfoResponse =
            await Risksystem3PublicAuthService.fetchFastAPIUserInfo(
              fastAPI2faLoginResponse.loginResponse,
              fastAPI2faLoginResponse.decryptedToken.user_id,
            );

          handleLoginSuccess(
            dispatch,
            newAccessToken,
            userInfoResponse.data.user_name,
            sessionResponse.data.client_name ?? '',
            fastAPI2faLoginResponse.loginResponse.data.config_name ?? '',
          );
        } catch (error) {
          if (error instanceof FastApiFetchError) {
            Risksystem3PublicAuthService.FastAPILogout();
            Cookies.remove('user_id');

            reportError(
              '2FA Login Failed - FastAPI',
              error,
              ErrorType.AUTHENTICATION_ERROR,
              {
                username: credentials.username,
                detail:
                  'FastAPI user info failed after successful 2FA and session bootstrap',
                sessionBootstrapResponse: sessionResponse.data,
                fastAPIResponse: fastAPI2faLoginResponse.loginResponse?.data,
              },
            );

            return dispatch(
              authenticationFailed(
                error.responseData || { detail: error.message },
              ),
            );
          }
          throw error;
        }
      } else {
        dispatch(unauthenticate());

        reportError(
          '2FA Login Failed - Legacy Session Bootstrap',
          new Error(
            `login_return_code: ${sessionResponse.data.login_return_code}`,
          ),
          ErrorType.AUTHENTICATION_ERROR,
          {
            username: credentials.username,
            detail: 'Session bootstrap failed with non-zero return code',
            sessionBootstrapResponse: sessionResponse.data,
            fastAPIResponse: fastAPI2faLoginResponse.loginResponse?.data,
          },
        );
      }
    } catch (error) {
      const axiosError = error as AxiosError;
      const errorResponse = axiosError.response?.data || {
        detail: 'There was an error logging in.',
      };

      reportError(
        '2FA Login Failed - Exception',
        error,
        ErrorType.AUTHENTICATION_ERROR,
        {
          username: credentials.username,
          detail: 'Exception occurred during 2FA login',
          errorResponse,
        },
      );

      dispatch(
        twoFactorAuthenticationFailed(
          errorResponse,
          credentials.username,
          credentials.accessToken || '',
          credentials.authKey || '',
          false,
          credentials.isEmailTokenRequired,
          credentials.isQrCodeRequired,
        ),
      );
    }
  };
}

// Helper function to handle 2FA scenarios
function handleTwoFactorAuthentication(
  dispatch: Dispatch<AuthenticationAction, {}, any>,
  username: string,
  accessToken: string,
  authKey: string,
  decodedToken: any,
) {
  // Case where 2FA is going to be required.
  // Check what kind of authentication Is required.
  if (decodedToken.is_email_needed) {
    dispatch(
      requestVerficationCodeEmail({
        username,
        accessToken,
        authKey,
        isQrCodeRequired: decodedToken.is_qr_code_needed || false,
        isEmailTokenRequired: decodedToken.is_email_needed,
      }),
    );
  } else if (decodedToken.is_qr_code_needed) {
    // TODO: Implement QR code login
    dispatch(
      authenticationFailed({ detail: 'QR code login is not yet setup.' }),
    );
  } else {
    dispatch(
      authenticationFailed({ detail: 'There was an error logging in.' }),
    );
  }
}

// Helper function to handle login errors
function handleLoginErrorResponse(
  dispatch: Dispatch<AuthenticationAction, {}, any>,
  error: any,
) {
  const errorStatus = error.response ? error.response.status : null;
  const errorData = error.response ? error.response.data : null;

  // FastAPI login may have succeeded before legacy session bootstrap failed (e.g. /api_session).
  Risksystem3PublicAuthService.FastAPILogout();
  Cookies.remove('user_id');

  if (errorStatus === 401 || errorStatus === 403) {
    dispatch(authenticationFailed(errorData));
  } else {
    dispatch(
      authenticationFailed({ detail: 'There was an error logging in.' }),
    );
  }
}

// ------------------ 2FA Authentication Actions ------------------ //

export function authenticationWith2FAInProgress(
  emailTokenRequired: boolean,
  qrCodeRequired: boolean,
  user_id: string,
  client_name: string,
  config_name: string,
  accessToken: string,
  authKey: string,
): IAuthenticationWith2FAInProgress {
  return {
    type: AUTHENTICATION_WITH_2FA_IN_PROGRESS,
    qrCodeRequired: qrCodeRequired,
    emailTokenRequired: emailTokenRequired,
    user_id: user_id,
    client_name: client_name,
    config_name: config_name,
    accessToken: accessToken,
    authKey: authKey,
  };
}

export function cancel2FALogin(): ICancel2FAInProgress {
  return {
    type: CANCEL_TWO_FACTOR_AUTHENTICATION_IN_PROGRESS,
  };
}

export function authenticateWith2FA(
  user_id: string,
  client_name: string,
  config_name: string,
  accessToken: string,
  authKey: string,
  emailSent: boolean,
  emailTokenRequired: boolean,
  qrCodeRequired: boolean,
): IAuthenticateWith2FA {
  return {
    type: AUTHENTICATE_WITH_2FA,
    user_id: user_id,
    client_name: client_name,
    config_name: config_name,
    accessToken: accessToken,
    authKey: authKey,
    qrCodeRequired: qrCodeRequired,
    emailTokenRequired: emailTokenRequired,
    emailSent: emailSent,
  };
}

export function twoFactorAuthenticationFailed(
  errorMessage: AuthErrorObject,
  user_id: string,
  accessToken: string,
  authKey: string,
  emailSent: boolean,
  emailTokenRequired: boolean,
  qrCodeRequired: boolean,
) {
  return {
    type: TWO_FACTOR_AUTHENTICATION_FAILED,
    errorMessage: errorMessage,
    user_id: user_id,
    accessToken: accessToken,
    authKey: authKey,
    emailSent: emailSent,
    emailTokenRequired: emailTokenRequired,
    qrCodeRequired: qrCodeRequired,
  };
}

export function requestVerficationCodeEmail(
  verficiationDetails: VerficationDetails,
) {
  return async (dispatch: Dispatch<AuthenticationAction, {}, any>) => {
    // Set authentication in progress.
    dispatch(
      authenticationWith2FAInProgress(
        verficiationDetails.isEmailTokenRequired,
        verficiationDetails.isQrCodeRequired,
        verficiationDetails.username,
        '',
        '',
        verficiationDetails.accessToken,
        verficiationDetails.authKey,
      ),
    );

    // Create an object of formData
    const formData = new FormData();
    formData.append('auth_key', verficiationDetails.authKey);

    FastAPIInstance.post(
      '/v1/get_verification_code?auth_type=user_email',
      formData,
      {
        headers: {
          Accept: 'application/json',
          'content-type': 'multipart/form-data',
          Authorization: `Bearer ${verficiationDetails.accessToken}`,
        },
      },
    )
      .then((response) => {
        if (response.status === 200) {
          dispatch(
            authenticateWith2FA(
              verficiationDetails.username,
              '',
              '',
              verficiationDetails.accessToken,
              verficiationDetails.authKey,
              true,
              verficiationDetails.isEmailTokenRequired,
              verficiationDetails.isQrCodeRequired,
            ),
          );
        }
      })
      .catch(() => {
        dispatch(
          twoFactorAuthenticationFailed(
            {
              detail:
                'An error occurred while attempting to send the verification code to your email.',
            },
            verficiationDetails.username,
            verficiationDetails.accessToken,
            verficiationDetails.authKey,
            false,
            verficiationDetails.isEmailTokenRequired,
            verficiationDetails.isQrCodeRequired,
          ),
        );
      });
  };
}

// Function to allow the user to have the QR code resent to their email.
export function requestQRCodeEmail(verficiationDetails: VerficationDetails) {
  return async (dispatch: Dispatch<AuthenticationAction, {}, any>) => {
    // Set authentication in progress.
    dispatch(
      authenticationWith2FAInProgress(
        verficiationDetails.isEmailTokenRequired,
        verficiationDetails.isQrCodeRequired,
        verficiationDetails.username,
        '',
        '',
        verficiationDetails.accessToken,
        verficiationDetails.authKey,
      ),
    );

    RisksystemHgAuthService.requireQrCode(
      verficiationDetails.authKey,
      verficiationDetails.accessToken,
    )
      .then((qr_response) => {
        if (qr_response.data.status === 200) {
          dispatch(
            authenticateWith2FA(
              verficiationDetails.username,
              '',
              '',
              verficiationDetails.accessToken,
              verficiationDetails.authKey,
              true, // Indicate that the QR code was sent.
              verficiationDetails.isEmailTokenRequired,
              verficiationDetails.isQrCodeRequired,
            ),
          );
        }
      })
      .catch((err) => {
        dispatch(
          twoFactorAuthenticationFailed(
            {
              detail: 'An error occured while attempting to send QR code.',
            },
            verficiationDetails.username,
            verficiationDetails.accessToken,
            verficiationDetails.authKey,
            false,
            verficiationDetails.isEmailTokenRequired,
            verficiationDetails.isQrCodeRequired,
          ),
        );
      });
  };
}
